import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from '../apiBase';
import { fullViewStorageKey } from '../lib/helpTutorialStorage';

const HelpCenterContext = createContext(null);

export const useHelpCenter = () => {
  const ctx = useContext(HelpCenterContext);
  if (!ctx) {
    throw new Error('useHelpCenter must be used within HelpCenterProvider');
  }
  return ctx;
};

export const HelpCenterProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const refreshSummary = useCallback(async () => {
    if (!token) {
      setSummary(null);
      return;
    }
    setSummaryLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/help-center/summary`);
      setSummary(data);
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  /** Un autre onglet (ex. tutoriel agrandi) a mis à jour les « vus » — rafraîchir les compteurs. */
  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    const key = fullViewStorageKey(uid);
    const onStorage = (e) => {
      if (e.key === key) {
        refreshSummary();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user?.id, refreshSummary]);

  const openHelp = useCallback(() => setOpen(true), []);
  const closeHelp = useCallback(() => setOpen(false), []);

  return (
    <HelpCenterContext.Provider
      value={{
        open,
        openHelp,
        closeHelp,
        setOpen,
        summary,
        summaryLoading,
        refreshSummary,
      }}
    >
      {children}
    </HelpCenterContext.Provider>
  );
};
